import React, { useState, useEffect } from "react";
import { FileOutlined, HomeOutlined } from "@ant-design/icons";
import { apiFetch } from "../../../core/utils/api";
import InfoIcon from "../InfoIcon";
import { typography, textColors, surfaces, borders, interactive } from "../../../design-system/tokens";

const WebsitePageField = ({ value, onChange, label, websiteId, infoText, help }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle both cases: value can be an ID (integer) or an object with id
  const selectedId = typeof value === 'object' && value !== null ? value.id : value;

  useEffect(() => {
    if (!websiteId) return;

    const fetchPages = async () => {
      setLoading(true);
      try {
        const resp = await apiFetch(`/api/websites/${websiteId}/pages/?mode=skeleton`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (resp.ok) {
          const data = await resp.json();
          setOptions(data);
        } else {
          console.error("Failed to fetch pages:", resp.status);
        }
      } catch (error) {
        console.error("Error fetching pages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPages();
  }, [websiteId]);

  if (loading) {
    return (
      <div className="field-wrapper">
        <div className={`flex items-center gap-2 py-3 ${typography.body} ${textColors.secondary}`}>
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span>Loading pages…</span>
        </div>
      </div>
    );
  }

  const selectedPage = options.find((p) => p && p.id === selectedId);

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span> : null}
        </label>
      )}

      {/* Card-style page selector */}
      <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-1.5 space-y-1`}>
        {options.length === 0 ? (
          <div className={`flex items-center gap-2 p-3 rounded-lg ${surfaces.primary} ${textColors.tertiary} ${typography.bodySmall}`}>
            <FileOutlined />
            <span>No pages available</span>
          </div>
        ) : (
          options.map((page) => {
            const isSelected = page.id === selectedId;
            const displayName = page.name || `Page ${page.id}`;
            const isHome = !page.subdomain;
            const subdomainDisplay = isHome ? "Home" : `/${page.subdomain}`;
            const PageIcon = isHome ? HomeOutlined : FileOutlined;

            return (
              <button
                key={page.id}
                type="button"
                onClick={() => {
                  if (isSelected) {
                    onChange(null);
                  } else {
                    onChange(page);
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                  isSelected
                    ? `${interactive.selectedBg} text-white shadow-sm`
                    : `${surfaces.primary} ${textColors.primary} ${surfaces.hover}`
                }`}
                aria-pressed={isSelected}
              >
                <PageIcon className={`text-base ${isSelected ? "text-white/80" : textColors.tertiary}`} />
                <div className="min-w-0 flex-1">
                  <div className={`${typography.bodySmall} font-medium truncate ${isSelected ? "text-white" : textColors.primary}`}>
                    {displayName}
                  </div>
                  <div className={`${typography.caption} truncate ${isSelected ? "text-white/70" : textColors.tertiary}`}>
                    {subdomainDisplay}
                  </div>
                </div>
                {isSelected && (
                  <svg className="w-4 h-4 text-white/80 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })
        )}
      </div>
      {help && <div className={`mt-1.5 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
};

export default WebsitePageField;
